import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { FilterQueryEditor } from '@viewer/filter/FilterQueryEditor'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import {
  describeFilterQuery,
  newFilterQuery,
} from '@viewer/filter/filterQuery'
import {
  categoryCounts,
  type ClashModelInventory,
  type ClashSide,
} from './clashSets'

// ---------------------------------------------------------------------------
// Editor de um lado da matriz A x B.
//
// Um lado sao modelos + categorias, a seleccao actual do viewer, ou uma
// REGRA. As categorias vem com CONTAGENS REAIS do modelo ("IFCBEAM
// (312)"), porque uma lista generica de classes IFC obriga a adivinhar o
// que o ficheiro tem mesmo e a maior parte nao tem.
//
// A regra e o construtor de filtros do viewer (Shift+F), tal e qual: os
// mesmos criterios (entidade, piso, sistema, material, tipo,
// PredefinedType, propriedade), os mesmos operadores e o mesmo E/OU.
// Nao ha linguagem nova para aprender. A contagem no cabecalho do lado e
// em vivo e ja conta so os elementos COM GEOMETRIA, que sao os unicos que
// podem colidir.
//
// A lista de categorias e longa (um IFC de escola tem 30 e tal classes),
// por isso ha caixa de procura, "todas"/"nenhuma" e um resumo colapsado
// quando o lado ja esta definido: o painel vive por cima do 3D e nao
// pode ocupar o ecra todo.
// ---------------------------------------------------------------------------

export interface ClashSetsEditorProps {
  label: string
  side: ClashSide
  onChange: (side: ClashSide) => void
  inventories: ClashModelInventory[]
  count: number
  selectionCount: number
  /** Uma regra esta a correr: a contagem ainda nao e a final. */
  ruleBusy?: boolean
}

export default function ClashSetsEditor({
  label,
  side,
  onChange,
  inventories,
  count,
  selectionCount,
  ruleBusy = false,
}: ClashSetsEditorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { nomeDe } = useNomesDosModelos()

  const cats = useMemo(
    () => categoryCounts(side.models, inventories),
    [side.models, inventories],
  )
  const visible = useMemo(() => {
    const q = search.trim().toUpperCase()
    return q ? cats.filter((c) => c.category.includes(q)) : cats
  }, [cats, search])

  const chosen = new Set(side.categories)
  const resumo =
    side.source === 'selection'
      ? `seleção atual (${selectionCount})`
      : side.source === 'rule'
        ? describeFilterQuery(side.rule)
        : side.categories.length === 0
          ? 'sem categorias escolhidas'
          : `${side.categories.length} categoria${side.categories.length === 1 ? '' : 's'}`
  const modelosResumo =
    side.models.length === 0
      ? `${inventories.length} modelo${inventories.length === 1 ? '' : 's'}`
      : `${side.models.length} modelo${side.models.length === 1 ? '' : 's'}`

  const toggleModel = (modelId: string) => {
    const has = side.models.includes(modelId)
    onChange({
      ...side,
      models: has
        ? side.models.filter((m) => m !== modelId)
        : [...side.models, modelId],
    })
  }

  const toggleCategory = (category: string) => {
    const has = chosen.has(category)
    onChange({
      ...side,
      categories: has
        ? side.categories.filter((c) => c !== category)
        : [...side.categories, category],
    })
  }

  return (
    <div className="rounded border border-[var(--color-border)]/70 bg-[var(--color-bg)]/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px]"
        aria-expanded={open}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="font-medium text-[var(--color-text)]">{label}</span>
        <span className="truncate text-[var(--color-text-dim)]">
          {modelosResumo} · {resumo}
        </span>
        <span
          className={`ml-auto shrink-0 font-mono ${count > 0 ? 'text-[var(--color-text-muted)]' : 'text-[var(--color-warn-text)]'}`}
          title={`${count} elemento${count === 1 ? '' : 's'} com geometria neste conjunto`}
        >
          {side.source === 'rule' && ruleBusy ? '…' : count}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-2 border-t border-[var(--color-border)]/50 px-2 py-2 text-[11px]">
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-1">
              <input
                type="radio"
                checked={side.source === 'categories'}
                onChange={() => onChange({ ...side, source: 'categories' })}
                className="accent-[var(--color-accent)]"
              />
              Por categoria
            </label>
            <label
              className="flex cursor-pointer items-center gap-1"
              title="Usa os elementos que estão selecionados no 3D neste momento."
            >
              <input
                type="radio"
                checked={side.source === 'selection'}
                onChange={() => onChange({ ...side, source: 'selection' })}
                className="accent-[var(--color-accent)]"
              />
              Seleção atual ({selectionCount})
            </label>
            <label
              className="flex cursor-pointer items-center gap-1"
              title="Define o conjunto com uma regra: o mesmo construtor do Filtro (Shift+F)."
            >
              <input
                type="radio"
                checked={side.source === 'rule'}
                onChange={() =>
                  onChange({
                    ...side,
                    source: 'rule',
                    rule: side.rule ?? newFilterQuery(),
                  })
                }
                className="accent-[var(--color-accent)]"
              />
              Por regra
            </label>
          </div>

          {side.source === 'rule' && (
            <div className="flex flex-col gap-1">
              <FilterQueryEditor
                query={side.rule ?? newFilterQuery()}
                onChange={(rule) => onChange({ ...side, rule })}
              />
              <p className="text-[10px] leading-snug text-[var(--color-text-dim)]">
                {ruleBusy
                  ? 'A contar os elementos da regra…'
                  : `A regra apanha ${count} elemento${count === 1 ? '' : 's'} com geometria. A regra fica guardada com o teste.`}
              </p>
            </div>
          )}

          {inventories.length > 1 && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[var(--color-text-dim)]">
                Modelos (nenhum marcado = todos)
              </span>
              {inventories.map((inv) => (
                <label
                  key={inv.modelId}
                  className="flex cursor-pointer items-center gap-1"
                >
                  <input
                    type="checkbox"
                    checked={side.models.includes(inv.modelId)}
                    onChange={() => toggleModel(inv.modelId)}
                    className="accent-[var(--color-accent)]"
                  />
                  <span className="truncate" title={nomeDe(inv.modelId)?.completo ?? inv.fileName}>
                    {nomeDe(inv.modelId)?.curto ?? inv.fileName}
                  </span>
                  <span className="ml-auto font-mono text-[10px] text-[var(--color-text-dim)]">
                    {inv.total}
                  </span>
                </label>
              ))}
            </div>
          )}

          {side.source === 'categories' && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Procurar categoria"
                  className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5"
                />
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...side,
                      categories: visible.map((c) => c.category),
                    })
                  }
                  className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ ...side, categories: [] })}
                  className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
                >
                  Nenhuma
                </button>
              </div>
              <div className="max-h-40 overflow-y-auto rounded border border-[var(--color-border)]/50">
                {visible.length === 0 && (
                  <p className="px-2 py-1 italic text-[var(--color-text-dim)]">
                    Sem categorias com geometria.
                  </p>
                )}
                {visible.map((c) => (
                  <label
                    key={c.category}
                    className="flex cursor-pointer items-center gap-1 px-1.5 py-0.5 hover:bg-[var(--color-bg)]"
                  >
                    <input
                      type="checkbox"
                      checked={chosen.has(c.category)}
                      onChange={() => toggleCategory(c.category)}
                      className="accent-[var(--color-accent)]"
                    />
                    <span className="truncate">{c.category}</span>
                    <span className="ml-auto font-mono text-[10px] text-[var(--color-text-dim)]">
                      {c.count}
                    </span>
                  </label>
                ))}
              </div>
              {side.categories.length === 0 && (
                <p className="text-[var(--color-warn-text)]">
                  Sem categorias marcadas este conjunto fica vazio. Carrega
                  em &quot;Todas&quot; ou escolhe as que interessam.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
